import React from 'react';
import {
    Box,
    Typography,
    Card,
    Button,
    Alert,
} from '@mui/material';
import {
    PhoneIphone,
    Tablet,
    Info,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';

interface BannerPreviewProps {
    data: any;
}

const BannerPreview: React.FC<BannerPreviewProps> = ({ data }) => {
    const translate = useTranslate();

    const renderIcon = (iconName: string, color: string) => {
        // Map common icon names to Material UI icons
        const iconMap: Record<string, string> = {
            'gift': '🎁',
            'star': '⭐',
            'lightning-bolt': '⚡',
            'crown': '👑',
            'fire': '🔥',
            'heart': '❤️',
            'diamond': '💎',
            'rocket': '🚀',
            'trophy': '🏆',
            'medal': '🏅',
        };

        return (
            <Box
                sx={{
                    fontSize: 24,
                    color: color || '#FFFFFF',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                }}
            >
                {iconMap[iconName] || '📱'}
            </Box>
        );
    };

    const renderBannerContent = () => {
        if (!data.title && !data.subtitle) {
            return (
                <Alert severity="info" sx={{ m: 2 }}>
                    <Typography variant="body2">
                        {translate('resources.banners.preview.enterContent')}
                    </Typography>
                </Alert>
            );
        }

        const bannerStyle = {
            width: '100%',
            aspectRatio: '2/1',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative' as const,
            display: 'flex',
            alignItems: 'center',
            padding: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        };

        if (data.display_type === 'image' && data.image_url) {
            return (
                <Box
                    sx={{
                        ...bannerStyle,
                        backgroundImage: `url(${data.image_url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        color: '#FFFFFF',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))',
                        }}
                    />
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {data.title && (
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.5,
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                }}
                            >
                                {data.title}
                            </Typography>
                        )}
                        {data.subtitle && (
                            <Typography
                                variant="body2"
                                sx={{
                                    opacity: 0.9,
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                                }}
                            >
                                {data.subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            );
        } else {
            // Gradient banner
            const gradient = data.gradient_start_color && data.gradient_end_color
                ? `linear-gradient(${data.gradient_angle || 135}deg, ${data.gradient_start_color}, ${data.gradient_end_color})`
                : 'linear-gradient(135deg, #FF6B6B, #4ECDC4)';

            return (
                <Box
                    sx={{
                        ...bannerStyle,
                        background: gradient,
                        color: '#FFFFFF',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        {data.title && (
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.5,
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                }}
                            >
                                {data.title}
                            </Typography>
                        )}
                        {data.subtitle && (
                            <Typography
                                variant="body2"
                                sx={{
                                    opacity: 0.9,
                                    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                                }}
                            >
                                {data.subtitle}
                            </Typography>
                        )}
                    </Box>
                    {data.icon_name && (
                        <Box sx={{ ml: 2 }}>
                            {renderIcon(data.icon_name, data.icon_color)}
                        </Box>
                    )}
                </Box>
            );
        }
    };

    return (
        <Box>
            {/* Device Frame */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <PhoneIphone sx={{ fontSize: 32, color: '#666', mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    {translate('resources.banners.preview.mobilePreview')}
                </Typography>
            </Box>

            {/* Phone Frame */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 300,
                    mx: 'auto',
                    backgroundColor: '#000',
                    borderRadius: 4,
                    p: 1,
                    position: 'relative',
                }}
            >
                {/* Screen */}
                <Box
                    sx={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: 3,
                        p: 2,
                        minHeight: 200,
                    }}
                >
                    {/* Status Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            px: 1,
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            9:41
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Box sx={{ width: 4, height: 4, backgroundColor: '#000', borderRadius: '50%' }} />
                            <Box sx={{ width: 4, height: 4, backgroundColor: '#000', borderRadius: '50%' }} />
                            <Box sx={{ width: 4, height: 4, backgroundColor: '#000', borderRadius: '50%' }} />
                        </Box>
                    </Box>

                    {/* App Header */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                            Home
                        </Typography>
                    </Box>

                    {/* Banner Preview */}
                    <Box sx={{ mb: 2 }}>
                        {renderBannerContent()}
                    </Box>

                    {/* Action Info */}
                    {data.action_type && data.action_type !== 'none' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">
                                <strong>Action:</strong> {data.action_type}
                                {data.action_target && ` → ${data.action_target}`}
                            </Typography>
                        </Alert>
                    )}
                </Box>
            </Box>

            {/* Banner Info */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#F9F9F9', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Info sx={{ fontSize: 16, color: '#666', mr: 1 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Banner Information
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" component="div">
                    <div>Type: {data.display_type || 'image'}</div>
                    {data.position !== undefined && <div>Position: {data.position}</div>}
                    {data.target_type && <div>Targeting: {data.target_type}</div>}
                    {data.display_frequency && <div>Frequency: {data.display_frequency}</div>}
                </Typography>
            </Box>
        </Box>
    );
};

export default BannerPreview;